// This file contains logic around requesting media download URLs.
// It's wrapped with a custom caching/deduping layer (see `./cache.ts`).
// Very similar to `./directURL.ts`.

import {isNotNullOrUndefined} from '@wandb/weave/common/util/types';

import {
  type DirectUrlQueryQuery,
  type DirectUrlQueryQueryVariables,
} from '../../../../generated/graphql';
import {DIRECT_URL_QUERY} from '../../../../graphql/media';
import {type RunSignature} from '../../../../types/run';
import {type Thumbnails} from '../../../MediaCard';
import {getKeyForFilePath} from '../keys';
import {fetchGQL} from '../util';
import {type BaseFetchParams} from '.';
import {fetchWithCache} from './cacheNew';

const directURLPromiseByFilePathKey: Map<string, Promise<string>> = new Map();
const internalURLPromiseByFilePathKey: Map<string, Promise<string>> = new Map();
const thumbnailsPromiseByFilePathKey: Map<
  string,
  Promise<Thumbnails>
> = new Map();
const hlsSrcPromiseByFilePathKey: Map<
  string,
  Promise<string | null | undefined>
> = new Map();

/**
 * Clear cached direct URL (and related) promises for a run+filePath so the next
 * fetch will re-run DirectUrlQuery and get fresh URLs (e.g. after load error / expired URL).
 */
export function invalidateDirectURLCacheForFilePath(
  runSignature: RunSignature,
  filePath: string
): void {
  const key = getKeyForFilePath(runSignature, filePath);
  directURLPromiseByFilePathKey.delete(key);
  internalURLPromiseByFilePathKey.delete(key);
  thumbnailsPromiseByFilePathKey.delete(key);
  hlsSrcPromiseByFilePathKey.delete(key);
}

export async function fetchDirectURLByFilePath({
  runSignature,
  filePaths,
}: BaseFetchParams) {
  return fetchWithCache({
    runSignature,
    filePaths,
    directAssetUrlPromisesByKey: directURLPromiseByFilePathKey,
    internalAssetUrlPromisesByKey: internalURLPromiseByFilePathKey,
    thumbnailsAssetUrlPromisesByKey: thumbnailsPromiseByFilePathKey,
    hlsSrcAssetUrlPromisesByKey: hlsSrcPromiseByFilePathKey,
    fetchNew: fetchDirectURLByFilePathUncached,
  });
}

async function fetchDirectURLByFilePathUncached({
  runSignature,
  filePaths,
}: BaseFetchParams): Promise<
  [
    Map<string, string>,
    Map<string, string>,
    Map<string, Thumbnails>,
    Map<string, string | null | undefined>,
  ]
> {
  if (filePaths.length === 0) {
    return [new Map(), new Map(), new Map(), new Map()];
  }

  const data = await fetchGQL<
    DirectUrlQueryQueryVariables,
    DirectUrlQueryQuery
  >({
    query: DIRECT_URL_QUERY,
    variables: {
      ...runSignature,
      filenames: filePaths,
    },
  });

  const files =
    data?.project?.run?.files?.edges
      .map(e => e.node)
      .filter(isNotNullOrUndefined) ?? [];

  return [
    new Map(files.map(f => [f.name, f.directUrl])),
    new Map(files.map(f => [f.name, f.url || ''])),
    new Map(files.map(f => [f.name, f.thumbnails])),
    new Map(files.map(f => [f.name, f.hlsSrc])),
  ];
}
